/**
 * Updates a URL parameter based on the provided name and value.
 * If the value is null or undefined, the parameter is removed from the URL.
 * Otherwise, the parameter is set to the given value.
 *
 * @param {string} paramName - The name of the parameter to update.
 * @param {string|null} value - The value to set for the parameter, or null to remove it.
 */
export const updateUrlParam = (paramName: string, value?: string | boolean | number | null) => {
  const currentUrl = new URL(window.location.href);
  if (value === null || value === undefined) {
    currentUrl.searchParams.delete(paramName);
  } else {
    currentUrl.searchParams.set(paramName, String(value));
  }
  window.history.replaceState({}, '', currentUrl);
};

/**
 * Checks for a specific URL parameter and returns its value.
 * If the parameter is not found or we're running server-side, returns null.
 *
 * @param {string} paramName - The name of the URL parameter to check.
 * @return {string|null} - The value of the parameter or null if not found or running server-side.
 */
export const getUrlParamValue = (paramName: string) => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const value = urlParams.get(paramName);
    return value;
  }
  return null;
};

/**
 * Constructs a URL path from the current location, removing specified query parameters.
 *
 * @param {Array<string>} paramsToDelete - List of parameter names to remove from the query string.
 * @returns {string} The URL path, including any remaining query parameters.
 */
export const getPathWithoutParams = (paramsToDelete: string[]) => {
  // Create a new URL object based on the current window location
  const url = new URL(window.location.href);

  // Delete the specified parameters from the URL's searchParams
  paramsToDelete.forEach((param) => url.searchParams.delete(param));

  // Construct the new path, including the search string if it's not empty
  const newPath = url.pathname + (url.search ? `${url.search}` : '');

  return newPath;
};
